import {
  useEffect,
  useId,
  useMemo,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  LogMealRequestSchema,
  MEAL_TYPES,
  mealTypeLabel,
  useAnalyzeText,
  useDeleteMealLog,
  useLogMeal,
  useMealLogs,
  useUpdateMealLog,
  type LogMealRequest,
  type MealLogged,
  type MealType,
  type UpdateMealLogRequest,
} from '@/lib/nutrition';
import { ConfirmDialog } from '@/features/settings/ConfirmDialog';
import { MealLogQuickAdd } from './MealLogQuickAdd';

/**
 * Vista "Mi diario" del módulo Nutrición.
 *
 * Decisiones:
 * - Range selector simple (7d / 30d). NO añadimos date pickers
 *   personalizados — 90% del uso esperado es ver "hoy/última semana".
 * - Timeline agrupada por día. Cada día muestra totales kcal/macros.
 * - "Añadir comida" abre un modal con form mínimo (mealType, datetime,
 *   description, kcal/macros opcionales).
 * - Edición inline: cada entry tiene un toggle "Editar" que reemplaza la
 *   fila por inputs; "Guardar" envía `useUpdateMealLog`. Más simple que un
 *   segundo modal, y respeta el contexto de la timeline.
 * - Borrar: `ConfirmDialog` reutilizado del módulo settings.
 */
export function MealLogView(): ReactElement {
  const [rangeDays, setRangeDays] = useState<7 | 30>(7);
  const [showAdd, setShowAdd] = useState(false);
  // M2.34.D — modal de captura rápida con IA (foto/texto).
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  const { fromISO, toISO } = useMemo(() => rangeFromDays(rangeDays), [rangeDays]);
  const query = useMealLogs(fromISO, toISO);
  const deleteMutation = useDeleteMealLog();

  function confirmDelete() {
    if (pendingDeleteId === null) return;
    deleteMutation.mutate(pendingDeleteId, {
      onSuccess: () => setPendingDeleteId(null),
    });
  }

  const grouped = useMemo(() => groupByDay(query.data ?? []), [query.data]);

  return (
    <section aria-label="Mi diario de comidas" className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div role="group" aria-label="Rango de fechas" className="flex gap-1 rounded-md border border-slate-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => setRangeDays(7)}
            aria-pressed={rangeDays === 7}
            className={
              rangeDays === 7
                ? 'rounded bg-teal-600 px-3 py-1 text-sm font-semibold text-white'
                : 'rounded px-3 py-1 text-sm text-slate-700 hover:bg-slate-100'
            }
          >
            Últimos 7 días
          </button>
          <button
            type="button"
            onClick={() => setRangeDays(30)}
            aria-pressed={rangeDays === 30}
            className={
              rangeDays === 30
                ? 'rounded bg-teal-600 px-3 py-1 text-sm font-semibold text-white'
                : 'rounded px-3 py-1 text-sm text-slate-700 hover:bg-slate-100'
            }
          >
            Últimos 30 días
          </button>
        </div>
        <div className="flex items-center gap-2">
          {/* M2.34.D — Añadir comida con análisis IA (foto o texto). */}
          <button
            type="button"
            onClick={() => setShowQuickAdd(true)}
            className="rounded-md border border-teal-600 bg-white px-4 py-2 text-sm font-semibold text-teal-700 shadow-sm hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
          >
            + Foto / Texto (IA)
          </button>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
          >
            Añadir comida
          </button>
        </div>
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando registros…
        </p>
      )}

      {query.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudieron cargar los registros. {query.error?.message ?? ''}
        </p>
      )}

      {query.data && grouped.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-600">
          No hay comidas registradas en este rango.
        </p>
      )}

      {grouped.length > 0 && (
        <ol className="space-y-4">
          {grouped.map((day) => (
            <DayBlock
              key={day.dateKey}
              dateKey={day.dateKey}
              entries={day.entries}
              editingId={editingId}
              onEdit={(id) => setEditingId(id)}
              onCancelEdit={() => setEditingId(null)}
              onDelete={(id) => setPendingDeleteId(id)}
            />
          ))}
        </ol>
      )}

      {showAdd && (
        <Modal title="Añadir comida" onClose={() => setShowAdd(false)}>
          <AddMealForm
            onSuccess={() => setShowAdd(false)}
            onCancel={() => setShowAdd(false)}
          />
        </Modal>
      )}

      {/* M2.34.D — modal de captura rápida con Claude (foto/texto). */}
      <MealLogQuickAdd
        open={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        onSuccess={() => setShowQuickAdd(false)}
      />

      <ConfirmDialog
        open={pendingDeleteId !== null}
        title="Borrar registro"
        description="Esta acción no se puede deshacer. La comida desaparecerá de tu diario."
        confirmLabel={deleteMutation.isPending ? 'Borrando…' : 'Borrar'}
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Day block (timeline)
// ---------------------------------------------------------------------------

interface DayGroup {
  dateKey: string;
  entries: MealLogged[];
}

function groupByDay(logs: MealLogged[]): DayGroup[] {
  const map = new Map<string, MealLogged[]>();
  for (const log of logs) {
    const key = dateOnlyFromTimestamp(log.datetime);
    const list = map.get(key);
    if (list) {
      list.push(log);
    } else {
      map.set(key, [log]);
    }
  }
  const out: DayGroup[] = [];
  for (const [dateKey, entries] of map) {
    // Más reciente del día primero.
    entries.sort((a, b) => parseTimestamp(b.datetime) - parseTimestamp(a.datetime));
    out.push({ dateKey, entries });
  }
  // Días más recientes primero.
  out.sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  return out;
}

function DayBlock({
  dateKey,
  entries,
  editingId,
  onEdit,
  onCancelEdit,
  onDelete,
}: {
  dateKey: string;
  entries: MealLogged[];
  editingId: number | null;
  onEdit: (id: number) => void;
  onCancelEdit: () => void;
  onDelete: (id: number) => void;
}): ReactElement {
  const totals = useMemo(() => sumMacros(entries), [entries]);
  return (
    <li className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 pb-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
          {dayLongFromKey(dateKey)}
        </h3>
        <p className="text-xs text-slate-700">
          <strong className="text-slate-900">{totals.kcal} kcal</strong>
          <span className="ml-2 text-slate-500">
            P {totals.protein_g}g · C {totals.carbs_g}g · G {totals.fat_g}g
          </span>
        </p>
      </header>
      <ul className="mt-3 space-y-2">
        {entries.map((e) => (
          <li key={e.id}>
            {editingId === e.id ? (
              <EditMealForm entry={e} onDone={onCancelEdit} onCancel={onCancelEdit} />
            ) : (
              <MealEntryRow
                entry={e}
                onEdit={() => onEdit(e.id)}
                onDelete={() => onDelete(e.id)}
              />
            )}
          </li>
        ))}
      </ul>
    </li>
  );
}

function MealEntryRow({
  entry,
  onEdit,
  onDelete,
}: {
  entry: MealLogged;
  onEdit: () => void;
  onDelete: () => void;
}): ReactElement {
  return (
    <article className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50/60 p-2">
      <span className="text-sm font-medium text-slate-900 w-14">
        {formatTime(entry.datetime, entry.timezone)}
      </span>
      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-700">
        {mealTypeLabel(entry.mealType)}
      </span>
      <p className="flex-1 min-w-[150px] text-sm text-slate-800">{entry.description}</p>
      {entry.kcal !== null && entry.kcal !== undefined && (
        <span className="text-xs text-slate-700">
          {entry.kcal} kcal
        </span>
      )}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          Editar
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded border border-rose-200 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-600 focus:ring-offset-1"
        >
          Borrar
        </button>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Add / Edit forms
// ---------------------------------------------------------------------------

function AddMealForm({
  onSuccess,
  onCancel,
}: {
  onSuccess: () => void;
  onCancel: () => void;
}): ReactElement {
  const mutation = useLogMeal();
  const [draft, setDraft] = useState<MealDraft>(() => emptyDraft());
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending) return;
    const candidate = draftToRequest(draft);
    const parsed = LogMealRequestSchema.safeParse(candidate);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Datos inválidos');
      return;
    }
    setError(null);
    mutation.mutate(parsed.data, { onSuccess });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <MealFormFields draft={draft} setDraft={setDraft} idPrefix="add" />
      {error && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
      {mutation.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo guardar. {mutation.error?.message ?? ''}
        </p>
      )}
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={mutation.isPending}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={mutation.isPending}
          aria-busy={mutation.isPending}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? 'Guardando…' : 'Guardar comida'}
        </button>
      </div>
    </form>
  );
}

function EditMealForm({
  entry,
  onDone,
  onCancel,
}: {
  entry: MealLogged;
  onDone: () => void;
  onCancel: () => void;
}): ReactElement {
  const mutation = useUpdateMealLog();
  const [draft, setDraft] = useState<MealDraft>(() => entryToDraft(entry));
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending) return;
    const candidate = draftToRequest(draft);
    const parsed = LogMealRequestSchema.safeParse(candidate);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Datos inválidos');
      return;
    }
    setError(null);
    const patch: UpdateMealLogRequest = parsed.data;
    mutation.mutate(
      { id: entry.id, patch },
      { onSuccess: onDone },
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="space-y-3 rounded-md border border-teal-200 bg-teal-50/40 p-3"
    >
      <MealFormFields draft={draft} setDraft={setDraft} idPrefix={`edit-${entry.id}`} compact />
      {error && (
        <p role="alert" className="rounded-md bg-rose-50 px-2 py-1 text-xs text-rose-800">
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={mutation.isPending}
          className="rounded border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={mutation.isPending}
          aria-busy={mutation.isPending}
          className="rounded bg-teal-600 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  );
}

interface MealDraft {
  datetimeLocal: string; // YYYY-MM-DDTHH:mm para <input type=datetime-local>
  mealType: MealType;
  description: string;
  kcal: string;
  protein_g: string;
  carbs_g: string;
  fat_g: string;
  notes: string;
}

function emptyDraft(): MealDraft {
  return {
    datetimeLocal: nowLocalDatetime(),
    mealType: 'lunch',
    description: '',
    kcal: '',
    protein_g: '',
    carbs_g: '',
    fat_g: '',
    notes: '',
  };
}

function entryToDraft(entry: MealLogged): MealDraft {
  return {
    datetimeLocal: isoToLocalDatetime(entry.datetime),
    mealType: entry.mealType,
    description: entry.description,
    kcal: entry.kcal !== null && entry.kcal !== undefined ? String(entry.kcal) : '',
    protein_g: entry.protein_g !== null && entry.protein_g !== undefined ? String(entry.protein_g) : '',
    carbs_g: entry.carbs_g !== null && entry.carbs_g !== undefined ? String(entry.carbs_g) : '',
    fat_g: entry.fat_g !== null && entry.fat_g !== undefined ? String(entry.fat_g) : '',
    notes: entry.notes ?? '',
  };
}

function draftToRequest(draft: MealDraft): LogMealRequest {
  const datetime = draft.datetimeLocal
    ? new Date(draft.datetimeLocal).toISOString()
    : new Date().toISOString();
  const body: LogMealRequest = {
    datetime,
    mealType: draft.mealType,
    description: draft.description.trim(),
  };
  const kcal = parsePositiveInt(draft.kcal);
  if (kcal !== null) body.kcal = kcal;
  const protein = parsePositiveInt(draft.protein_g);
  if (protein !== null) body.protein_g = protein;
  const carbs = parsePositiveInt(draft.carbs_g);
  if (carbs !== null) body.carbs_g = carbs;
  const fat = parsePositiveInt(draft.fat_g);
  if (fat !== null) body.fat_g = fat;
  const notes = draft.notes.trim();
  if (notes) body.notes = notes;
  return body;
}

function MealFormFields({
  draft,
  setDraft,
  idPrefix,
  compact,
}: {
  draft: MealDraft;
  setDraft: (next: MealDraft) => void;
  idPrefix: string;
  compact?: boolean;
}): ReactElement {
  function update<K extends keyof MealDraft>(key: K, value: MealDraft[K]) {
    setDraft({ ...draft, [key]: value });
  }

  // M2.38 — Autocompletar macros con IA desde la descripción del usuario.
  // Solo rellena los campos VACÍOS para no pisar valores ya editados.
  const analyzeMutation = useAnalyzeText();
  const canAutofill = draft.description.trim().length >= 3;
  const hasAnyMacro = Boolean(
    draft.kcal || draft.protein_g || draft.carbs_g || draft.fat_g,
  );

  function handleAutofill() {
    if (!canAutofill || analyzeMutation.isPending) return;
    // M2.38 — useAnalyzeText espera `{text: string}` (no string suelto).
    // Pasarlo como string causaba destructure `{text}=str` → text=undefined
    // → backend rechaza con 400 'Petición inválida'.
    analyzeMutation.mutate({ text: draft.description.trim() }, {
      onSuccess: (analysis) => {
        // Solo machacamos lo vacío. Si el usuario ya metió kcal manualmente,
        // respetamos su valor.
        const next: MealDraft = { ...draft };
        if (!draft.description.trim() && analysis.title) {
          next.description = analysis.title;
        }
        if (!draft.kcal && analysis.totalKcal > 0) {
          next.kcal = String(Math.round(analysis.totalKcal));
        }
        if (!draft.protein_g && analysis.macros.protG !== null && analysis.macros.protG !== undefined) {
          next.protein_g = String(Math.round(analysis.macros.protG));
        }
        if (!draft.carbs_g && analysis.macros.choG !== null && analysis.macros.choG !== undefined) {
          next.carbs_g = String(Math.round(analysis.macros.choG));
        }
        if (!draft.fat_g && analysis.macros.fatG !== null && analysis.macros.fatG !== undefined) {
          next.fat_g = String(Math.round(analysis.macros.fatG));
        }
        setDraft(next);
      },
    });
  }
  const sizeClass = compact
    ? 'rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600'
    : 'rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600';
  return (
    <>
      <div className={compact ? 'grid grid-cols-1 gap-2 sm:grid-cols-2' : 'grid grid-cols-1 gap-3 sm:grid-cols-2'}>
        <div>
          <label htmlFor={`${idPrefix}-datetime`} className="block text-xs font-medium text-slate-700">
            Fecha y hora
          </label>
          <input
            id={`${idPrefix}-datetime`}
            type="datetime-local"
            value={draft.datetimeLocal}
            onChange={(e) => update('datetimeLocal', e.target.value)}
            className={`mt-1 block w-full ${sizeClass}`}
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-mealtype`} className="block text-xs font-medium text-slate-700">
            Tipo de comida
          </label>
          <select
            id={`${idPrefix}-mealtype`}
            value={draft.mealType}
            onChange={(e) => update('mealType', e.target.value as MealType)}
            className={`mt-1 block w-full ${sizeClass}`}
          >
            {MEAL_TYPES.map((t) => (
              <option key={t} value={t}>
                {mealTypeLabel(t)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between gap-2">
          <label htmlFor={`${idPrefix}-description`} className="block text-xs font-medium text-slate-700">
            Descripción
          </label>
          {/* M2.38 — Autocompletar macros con IA desde la descripción. */}
          <button
            type="button"
            onClick={handleAutofill}
            disabled={!canAutofill || analyzeMutation.isPending}
            data-testid={`${idPrefix}-autofill-ia`}
            title={
              hasAnyMacro
                ? 'Rellenará SOLO los campos kcal/macros vacíos'
                : 'Analiza la descripción y rellena kcal/macros'
            }
            className="inline-flex items-center gap-1 rounded border border-teal-400 bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-800 hover:bg-teal-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span aria-hidden="true">✨</span>
            {analyzeMutation.isPending ? 'Analizando…' : 'Autocompletar con IA'}
          </button>
        </div>
        <input
          id={`${idPrefix}-description`}
          type="text"
          required
          maxLength={500}
          value={draft.description}
          onChange={(e) => update('description', e.target.value)}
          placeholder="Ej: tostada con aguacate y huevo"
          className={`mt-1 block w-full ${sizeClass}`}
        />
        {analyzeMutation.isError && (
          <p role="alert" className="mt-1 text-xs text-rose-700">
            No se pudo analizar. {analyzeMutation.error?.message ?? ''}
          </p>
        )}
        {analyzeMutation.isSuccess && (
          <p role="status" className="mt-1 text-xs text-emerald-700">
            ✓ Macros completados. Ajusta si hace falta y guarda.
          </p>
        )}
      </div>

      <div className={compact ? 'grid grid-cols-2 gap-2 sm:grid-cols-4' : 'grid grid-cols-2 gap-3 sm:grid-cols-4'}>
        <div>
          <label htmlFor={`${idPrefix}-kcal`} className="block text-xs font-medium text-slate-700">
            Kcal (opcional)
          </label>
          <input
            id={`${idPrefix}-kcal`}
            type="number"
            min={0}
            inputMode="numeric"
            value={draft.kcal}
            onChange={(e) => update('kcal', e.target.value)}
            className={`mt-1 block w-full ${sizeClass}`}
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-protein`} className="block text-xs font-medium text-slate-700">
            Proteína (g)
          </label>
          <input
            id={`${idPrefix}-protein`}
            type="number"
            min={0}
            inputMode="numeric"
            value={draft.protein_g}
            onChange={(e) => update('protein_g', e.target.value)}
            className={`mt-1 block w-full ${sizeClass}`}
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-carbs`} className="block text-xs font-medium text-slate-700">
            Carbohidratos (g)
          </label>
          <input
            id={`${idPrefix}-carbs`}
            type="number"
            min={0}
            inputMode="numeric"
            value={draft.carbs_g}
            onChange={(e) => update('carbs_g', e.target.value)}
            className={`mt-1 block w-full ${sizeClass}`}
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-fat`} className="block text-xs font-medium text-slate-700">
            Grasas (g)
          </label>
          <input
            id={`${idPrefix}-fat`}
            type="number"
            min={0}
            inputMode="numeric"
            value={draft.fat_g}
            onChange={(e) => update('fat_g', e.target.value)}
            className={`mt-1 block w-full ${sizeClass}`}
          />
        </div>
      </div>

      {!compact && (
        <div>
          <label htmlFor={`${idPrefix}-notes`} className="block text-xs font-medium text-slate-700">
            Notas (opcional)
          </label>
          <textarea
            id={`${idPrefix}-notes`}
            rows={2}
            maxLength={1000}
            value={draft.notes}
            onChange={(e) => update('notes', e.target.value)}
            className={`mt-1 block w-full ${sizeClass}`}
          />
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Modal (mismo patrón que RecipesView)
// ---------------------------------------------------------------------------

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}): ReactElement {
  const titleId = useId();

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-6"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
      >
        <header className="mb-4 flex items-start justify-between gap-4 border-b border-slate-200 pb-3">
          <h2 id={titleId} className="text-lg font-semibold text-slate-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-600"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface MacrosTotal {
  kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

function sumMacros(entries: MealLogged[]): MacrosTotal {
  return entries.reduce<MacrosTotal>(
    (acc, e) => ({
      kcal: acc.kcal + (e.kcal ?? 0),
      protein_g: acc.protein_g + (e.protein_g ?? 0),
      carbs_g: acc.carbs_g + (e.carbs_g ?? 0),
      fat_g: acc.fat_g + (e.fat_g ?? 0),
    }),
    { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 },
  );
}

function rangeFromDays(days: number): { fromISO: string; toISO: string } {
  const now = new Date();
  const to = new Date(now);
  to.setUTCHours(23, 59, 59, 999);
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  from.setUTCHours(0, 0, 0, 0);
  return { fromISO: from.toISOString(), toISO: to.toISOString() };
}

function parseTimestamp(value: string | number): number {
  if (typeof value === 'number') return value;
  const n = Number(value);
  if (Number.isFinite(n) && String(n) === value.trim()) return n;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function dateOnlyFromTimestamp(value: string | number): string {
  const ms = parseTimestamp(value);
  if (!ms) return String(value);
  return new Date(ms).toISOString().slice(0, 10);
}

function formatTime(value: string | number, tz: string): string {
  const ms = parseTimestamp(value);
  if (!ms) return '';
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toISOString().slice(11, 16);
  }
}

function dayLongFromKey(dateKey: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: 'UTC',
      weekday: 'long',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(`${dateKey}T00:00:00Z`));
  } catch {
    return dateKey;
  }
}

function nowLocalDatetime(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function isoToLocalDatetime(value: string | number): string {
  const ms = parseTimestamp(value);
  if (!ms) return nowLocalDatetime();
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function parsePositiveInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}
